// Auth state + token management.
// Stores the access token in memory ONLY (not localStorage) — the
// refresh cookie is httpOnly and persists across reloads. On app boot
// we call /api/auth/refresh once: if it succeeds the user is logged in,
// otherwise they're anonymous.

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, configureApi } from '../lib/api';
import { applyToolTierOverrides, applyCategories } from '../mock';
import { setSeoOverrides } from '../seo/seoStore';

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [token, setTokenState] = useState(null);
  const [user, setUser] = useState(null);
  const [booted, setBooted] = useState(false);

  const setToken = useCallback((t, u) => {
    setTokenState(t || null);
    if (u !== undefined) setUser(u || null);
  }, []);

  const logout = useCallback(async () => {
    try { await api('/api/auth/logout', { method: 'POST' }); } catch (_) {}
    setToken(null, null);
  }, [setToken]);

  // Wire the api helper to our state.
  useEffect(() => {
    configureApi({
      getToken: () => token,
      setToken: (t, u) => setToken(t, u),
      onAuthFail: () => setToken(null, null),
    });
  }, [token, setToken]);

  // Boot: try to refresh + load tool tier overrides in parallel.
  // The two calls are independent: a logged-out visitor still needs
  // the override map so home cards render the right Pro/Basic badge.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const base = process.env.REACT_APP_API_BASE || '';
      // Auth refresh
      try {
        const res = await fetch(`${base}/api/auth/refresh`, {
          method: 'POST', credentials: 'include',
        });
        if (!cancelled && res.ok) {
          const data = await res.json();
          setToken(data.access_token, data.user);
        }
      } catch (_) { /* offline / first visit */ }
      // Tool tier + SEO overrides (public, no auth needed). Both come
      // in the same payload to avoid a second round-trip on first paint.
      try {
        const res = await fetch(`${base}/api/tools`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          applyToolTierOverrides(data?.overrides || {});
          setSeoOverrides(data?.seo || {});
        }
      } catch (_) { /* fall back to catalogue defaults */ }
      // Home-page categories (public, no auth). Falls back to
      // DEFAULT_CATEGORIES baked into mock.js if the call fails.
      try {
        const res = await fetch(`${base}/api/categories`, { credentials: 'include' });
        if (!cancelled && res.ok) {
          const data = await res.json();
          applyCategories(data?.categories || []);
        }
      } catch (_) { /* fall back to default categories */ }
      if (!cancelled) setBooted(true);
    })();
    return () => { cancelled = true; };
  }, [setToken]);

  const login = useCallback(async (email, password) => {
    const data = await api('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    setToken(data.access_token, data.user);
    return data.user;
  }, [setToken]);

  const register = useCallback(async ({ email, password, name, locale }) => {
    const data = await api('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ email, password, name, locale }),
    });
    setToken(data.access_token, data.user);
    return data.user;
  }, [setToken]);

  const refresh = useCallback(async () => {
    try {
      const me = await api('/api/me');
      setUser(me);
      return me;
    } catch (_) { return null; }
  }, []);

  // Effective tier comes from the backend (deps.effective_tier mirrored
  // into UserPublic.tier). Anonymous visitors don't have a `me`
  // payload, so we default to 'anon' which the cascade rule treats as
  // the lowest rank. Useful for `userTierAllows(toolTier, tier)` calls
  // in the configurator and route guards.
  const value = {
    user, token, booted,
    isAuthed: !!user,
    isPro: !!user?.is_pro,
    isAdmin: !!user?.is_admin,
    plan: user?.plan || 'free',
    tier: user?.tier || (user ? 'free' : 'anon'),
    login, logout, register, refresh, setToken,
  };

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
}
